import { useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useQuery } from '@tanstack/react-query';
import { IconBuildingSkyscraper, IconCheck, IconChevronDown, IconSearch, IconWorld } from '@tabler/icons-react';
import { staffScopeSearch, tenantHome, type AppManifest } from '@infinix/registry';
import { tint, v } from '@infinix/ui';
import { accountApi } from './account';
import { useSession } from './session';

/**
 * The organization menu, top left on every page for Nest staff (A-35): "All organizations" (the platform: Ops), or
 * one organization and then one of its units. Picking an organization opens its Organization dashboard, as its admins
 * see it (A-34); every app in the launcher then works on it. Organizations come from Core's staff route set (N-7),
 * searched a page at a time. The choice goes into the address (`?org=`), and the shell switches the session to it.
 * In Ops (the platform) it always reads All organizations, whatever another tab has open.
 */
export function OrgPicker({ current, goTo }: { current: AppManifest | undefined; goTo: (path: string) => void }) {
  const s = useSession();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  const access = Boolean(s.view?.staffTenantAccess);
  const tenants = useQuery({ queryKey: ['staff-tenants', q.trim()], queryFn: () => accountApi.tenants(q.trim()), enabled: Boolean(anchor) && access });
  const platform = current?.audience === 'staff';
  const tenant = platform ? null : s.org;
  const unit = s.units.find((u) => u.id === s.buId) ?? null;
  const close = () => { setAnchor(null); setQ(''); };
  const pickTenant = (id: string | null) => {
    close();
    // All organizations is the platform (Ops); an organization opens on its dashboard.
    goTo(id === null ? '/ops' : tenantHome(id));
  };
  const pickUnit = (id: string | null) => {
    close();
    if (!tenant) return;
    // A unit narrows the app in view; its first page (a record of another unit wouldn't open).
    const base = current && current.audience === 'customer' && !current.everyone ? current.basePath : '/org';
    goTo(`${base}${staffScopeSearch('', { org: tenant.id, unit: id })}`);
  };
  const row = (key: string, label: string, selected: boolean, onClick: () => void, sub?: string) => (
    <Box
      key={key}
      component="button"
      type="button"
      onClick={onClick}
      sx={{ all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: '10px', p: '7px 10px', borderRadius: '7px', fontSize: 13, cursor: 'pointer', '&:hover': { background: v('sunk') }, '&:focus-visible': { outline: `2px solid ${v('accent')}` } }}
    >
      <Box sx={{ width: 14, display: 'flex' }}>{selected && <IconCheck size={14} stroke={2.6} color="var(--ix-accentText)" />}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
        {sub && <Box sx={{ fontSize: 11.5, color: v('caption') }}>{sub}</Box>}
      </Box>
    </Box>
  );
  const heading = (text: string) => <Box sx={{ px: '10px', pt: '8px', pb: '4px', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', color: v('caption') }}>{text}</Box>;
  const name = tenant?.name ?? 'All organizations';
  const sub = tenant ? (unit?.name ?? 'All units') : 'Platform';

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="dialog"
        aria-label={`Organization: ${name}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          all: 'unset', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: '9px', height: 40, p: '0 10px 0 6px', borderRadius: '10px',
          width: { xs: 170, md: 240 }, flexShrink: 0, border: `1px solid ${tint(v('staff'), 40)}`, background: tint(v('staff'), 8), cursor: 'pointer',
          '&:focus-visible': { outline: `2px solid ${v('accent')}` },
        }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: '7px', background: tint(v('staff'), 18), color: v('staff'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {tenant ? <IconBuildingSkyscraper size={15} aria-hidden /> : <IconWorld size={15} aria-hidden />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>
          <Box sx={{ fontSize: 11.5, color: v('caption'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</Box>
        </Box>
        <IconChevronDown size={14} color="var(--ix-caption)" />
      </Box>
      <Popover anchorEl={anchor} open={Boolean(anchor)} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { width: 300, mt: '6px', p: '6px', maxHeight: 480, display: 'flex', flexDirection: 'column' } } }}>
        {access && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 34, px: '10px', mx: '4px', mt: '4px', border: `1px solid ${v('border2')}`, borderRadius: '8px', background: v('sunk') }}>
            <IconSearch size={14} color="var(--ix-caption)" aria-hidden />
            <Box component="input" autoFocus value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} placeholder="Search organizations" aria-label="Search organizations" sx={{ all: 'unset', flex: 1, fontSize: 13 }} />
          </Box>
        )}
        <Box sx={{ overflowY: 'auto', mt: '4px' }}>
          {row('all', 'All organizations', tenant === null, () => pickTenant(null), 'Platform · Ops')}
          {!access ? (
            <Box sx={{ p: '8px 10px', fontSize: 12.5, color: v('caption') }}>Opening an organization needs Core's staff route set, which isn't configured here.</Box>
          ) : (
            <>
              {heading('ORGANIZATION')}
              {tenants.isPending ? <Box sx={{ p: '8px 10px', fontSize: 12.5, color: v('caption') }}>Loading…</Box>
                : tenants.isError ? <Box sx={{ p: '8px 10px', fontSize: 12.5, color: v('danger') }}>Couldn't load organizations.</Box>
                  : tenants.data.tenants.length === 0 ? <Box sx={{ p: '8px 10px', fontSize: 12.5, color: v('caption') }}>No organization matches.</Box>
                    : tenants.data.tenants.map((t) => row(t.id, t.name, tenant?.id === t.id, () => pickTenant(t.id)))}
              {tenants.data && tenants.data.total > tenants.data.tenants.length && (
                <Box sx={{ p: '6px 10px', fontSize: 11.5, color: v('caption') }}>Showing {tenants.data.tenants.length} of {tenants.data.total}. Search to narrow.</Box>
              )}
            </>
          )}
          {tenant && s.units.length > 0 && (
            <>
              {heading(`UNIT · ${tenant.name.toUpperCase()}`)}
              {row('bu-all', 'All units', s.buId === null, () => pickUnit(null))}
              {s.units.map((u) => row(u.id, u.name, s.buId === u.id, () => pickUnit(u.id)))}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
